import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Route, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RoutingPreviewCard = () => {
  const [role, setRole] = useState('sales');
  const [country, setCountry] = useState('India');
  const [tier, setTier] = useState('');
  const [result, setResult] = useState(null);
  const [tierResult, setTierResult] = useState(null);
  const [mismatch, setMismatch] = useState(null);
  const [busy, setBusy] = useState(null);

  const runMismatch = async (dryRun) => {
    if (!dryRun && !window.confirm('Hand every auto-assigned country-mismatched lead to Soul Sanskar (Super Admin)? Leads a rep created herself and manual transfers are left alone.')) return;
    setBusy(dryRun ? 'mismatch-dry' : 'mismatch');
    try {
      const res = await axios.post(`${API_URL}/api/data-repair/reassign-country-mismatched-leads`,
        { dry_run: dryRun }, { withCredentials: true, timeout: 300000 });
      setMismatch(res.data);
      toast.success(dryRun ? `${res.data.mismatched} mismatched owner(s) found` : `Reassigned ${res.data.reassigned} lead(s) to ${res.data.new_owner}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Repair failed');
    } finally { setBusy(null); }
  };

  const preview = async () => {
    setBusy('preview');
    try {
      const params = new URLSearchParams({ role, country });
      if (tier) params.append('tier', tier);
      const res = await axios.get(`${API_URL}/api/data-repair/routing-preview?${params}`, { withCredentials: true });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setBusy(null); }
  };

  const healTiers = async () => {
    if (!window.confirm('Give every lead a tier derived from its payments? Never downgrades an LMS tier. Idempotent.')) return;
    setBusy('tiers');
    try {
      const res = await axios.post(`${API_URL}/api/data-repair/heal-tiers`, {}, { withCredentials: true, timeout: 300000 });
      setTierResult(res.data);
      toast.success(`Tiers healed · scanned ${res.data.scanned}, changed ${res.data.changed}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tier heal failed');
    } finally { setBusy(null); }
  };

  return (
    <div className="card p-6" data-testid="routing-preview-card">
      <div className="flex items-center gap-2 mb-1">
        <Route className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-slate-900">Lead Routing — who gets the next one?</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Dry run of the round robin. Pool = active · accepting leads · country tag matches (or no tags) · opted-in to the tier.
        Country tags are strict — when nobody is tagged for the lead's country it goes to Soul Sanskar (Super Admin). Nothing is assigned here.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="input-base w-auto" data-testid="routing-role">
            <option value="sales">Sales (consultation bookings)</option>
            <option value="pre_sales">Pre-Sales (new leads)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Country</label>
          <input value={country} onChange={(e) => setCountry(e.target.value)} className="input-base w-40" data-testid="routing-country" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Lead tier</label>
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="input-base w-auto" data-testid="routing-tier">
            <option value="">Any / unknown</option>
            <option value="free">free</option>
            <option value="trial_paid">trial paid</option>
            <option value="soulai_paid">soulai paid</option>
            <option value="elite">elite</option>
          </select>
        </div>
        <button onClick={preview} disabled={busy === 'preview'} className="btn-primary" data-testid="routing-preview-btn">
          {busy === 'preview' ? 'Checking…' : 'Preview next assignment'}
        </button>
        <button onClick={healTiers} disabled={busy === 'tiers'} className="px-3 py-1.5 text-sm font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50 ml-auto inline-flex items-center gap-1.5" data-testid="heal-tiers-btn">
          <RefreshCw className="w-3.5 h-3.5" /> {busy === 'tiers' ? 'Healing…' : 'Heal lead tiers'}
        </button>
      </div>
      {result && (
        <div className="mt-4 rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid="routing-preview-result">
          <div className="text-sm">
            Next <b>{result.role.replace('_', '-')}</b> for <b>{result.country}</b>{result.tier ? <> · tier <b>{result.tier}</b></> : null}:{' '}
            {result.next ? <span className="font-semibold text-indigo-700" data-testid="routing-next-name">{result.next.full_name}</span> : <span className="text-rose-600 font-semibold">nobody available</span>}
          </div>
          {result.routed_to_founders_office && (
            <div className="text-xs text-amber-700 mt-1" data-testid="routing-founders-note">
              {result.founders_route_reason === 'us_locked'
                ? 'US leads always go to Soul Sanskar (Super Admin) — locked rule.'
                : 'No associate is tagged for this country — the lead goes to Soul Sanskar (Super Admin).'}
            </div>
          )}
          {result.tier_relaxed && <div className="text-xs text-amber-700 mt-1">Nobody in this country opted in to the tier — the tier filter was relaxed (country tags still apply).</div>}
          {!result.routed_to_founders_office && (
            <>
              <div className="mt-2 text-xs text-slate-500">Rotation order ({result.pool.length}): {result.pool.map(p => p.full_name).join(' → ') || '—'}</div>
              <div className="text-xs text-slate-500">Last assigned: {result.pool.find(p => p.user_id === result.last_assigned_agent_id)?.full_name || '—'}</div>
            </>
          )}
        </div>
      )}
      <div className="mt-4 border-t border-slate-100 pt-4">
        <div className="text-sm font-semibold text-slate-800">Country-mismatched owners</div>
        <p className="text-xs text-slate-500 mt-0.5 mb-2">
          Hands auto-assigned leads whose owner is not tagged for that country over to Soul Sanskar. Leads a rep created herself and manual TL/admin transfers are untouched.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => runMismatch(true)} disabled={!!busy}
            className="px-3 py-1.5 text-sm font-medium bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            data-testid="country-mismatch-dryrun-btn">
            {busy === 'mismatch-dry' ? 'Checking…' : 'Dry run'}
          </button>
          <button onClick={() => runMismatch(false)} disabled={!!busy}
            className="px-3 py-1.5 text-sm font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50"
            data-testid="country-mismatch-fix-btn">
            {busy === 'mismatch' ? 'Reassigning…' : 'Reassign to Soul Sanskar'}
          </button>
        </div>
        {mismatch && (
          <div className="mt-2 text-xs text-slate-600" data-testid="country-mismatch-result">
            Scanned {mismatch.scanned} leads · mismatched {mismatch.mismatched} · reassigned {mismatch.reassigned}
            {mismatch.samples?.length ? (
              <ul className="mt-1 space-y-0.5">
                {mismatch.samples.map((s) => (
                  <li key={`${s.lead_id}-${s.role}`}>
                    {s.lead || s.lead_id} · {s.country} · {s.role.replace('_', '-')} owner {s.from} (tagged {s.owner_tags.join(', ')})
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>
      {tierResult && <div className="mt-2 text-xs text-slate-600" data-testid="heal-tiers-result">Tiers: scanned {tierResult.scanned}, changed {tierResult.changed}</div>}
    </div>
  );
};

export default RoutingPreviewCard;
